import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Map, {
  NavigationControl,
  Marker,
  ViewState,
  GeolocateControl,
  Popup,
  MapRef,
  LngLatBounds,
  // AttributionControl,
} from "react-map-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "@/store";
import {
  getMapPins,
  getPinDetails,
  MapStyle,
  MarkerType,
  setMapState,
  setPinDetails,
  setSelectedMarker,
  TypeMapPins,
} from "./mapSlice";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import MapOptions from "../project-listing/ui/MapOptions";
import ProjectCard from "../project-listing/ui/ProjectCard";
import { IoMdCloseCircle } from "react-icons/io";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from "@/components/ui/carousel";
// import { PayloadAction } from "@reduxjs/toolkit";
// import { ScrollBar } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { ChevronDown, ChevronUp } from "lucide-react";
// import stage1Icon from "@/assets/markers/stage1.svg";
// import stage2Icon from "@/assets/markers/stage2.svg";
// import stage3Icon from "@/assets/markers/stage3.svg";
// import stage4Icon from "@/assets/markers/stage4.svg";
// import starPinStage1Icon from "@/assets/markers/stage1StarPin.png";
// import starPinStage2Icon from "@/assets/markers/stage2StarPin.png";
// import starPinStage3Icon from "@/assets/markers/stage3StarPin.png";
// import starPinStage4Icon from "@/assets/markers/stage4StarPin.png";

import { useSidebar } from "@/components/ui/sidebar";
import useDebounce from "@/hooks/useDebounce";
import { useLoading } from "@/contexts/LoadingContext";
// import mapboxgl from "mapbox-gl";

export const map_token = import.meta.env.VITE_MAPBOX_TOKEN;
const streetView = "mapbox://styles/mapbox/streets-v11";
const satelliteView = "mapbox://styles/mapbox/satellite-v9";
const outdoorView = "mapbox://styles/mapbox/outdoors-v11";
// const darkTheme = "mapbox://styles/mapbox/dark-v10";
// const lightTheme = "mapbox://styles/mapbox/light-v10";

interface MapViewProps {
  // projects: Project[];
  mapPins: TypeMapPins;
  isCompanyDetailHidden?: boolean;
}

interface PinDescription {
  title: string;
  rows: {
    subTitle: string;
    pin_color: string;
  }[];
}

// interface GetMapPinsResponse {
//   payload: {
//     projectLeads: any[];
//     totalCount: number;
//     nextPage: number;
//     limit: number;
//     pinDescription: {
//       title: string;
//       rows: {
//         subTitle: string;
//         pin_color: string;
//       }[];
//     };
//   };
//   meta: {
//     arg: {
//       params: Record<string, any>;
//       body: {
//         filters: {
//           coordinates: {
//             _sw: { lng: number; lat: number };
//             _ne: { lng: number; lat: number };
//           };
//         };
//       };
//     };
//     requestId: string;
//     requestStatus: string;
//   };
// }

const MapView: React.FC<MapViewProps> = ({
  mapPins,
  isCompanyDetailHidden,
}) => {
  const viewState = useSelector(
    (state: RootState) => state.rootReducer.mapViewReducer.mapState
  );
  const { popupAnchor, popupOffset } = useSelector(
    (state: RootState) => state.rootReducer.mapViewReducer.mapState
  );
  const mapStyle: MapStyle = useSelector(
    (state: RootState) => state.rootReducer.mapViewReducer.mapStyle
  );
  const showPop = useSelector(
    (state: RootState) => state.rootReducer.mapViewReducer.markerLeadDetails
  );

  const { open } = useSidebar();
  const mapRef = useRef<MapRef>(null);
  const mapContainerRef = useRef<HTMLDivElement>(null);

  const dispatch = useDispatch<AppDispatch>();
  const [mapMarkers, setMarkers] = useState<TypeMapPins>([]);
  const [currentBounds, setCurrentBounds] = useState<LngLatBounds | null>(null);
  const debouncedBounds = useDebounce<LngLatBounds | null>(currentBounds);
  const [pinDescription, setPinDescription] = useState<PinDescription | null>(
    null
  );

  const { setLoading } = useLoading();
  const geolocateRef = useRef<mapboxgl.GeolocateControl | null>(null);

  useEffect(() => {
    const pmarkers = [...mapPins];
    setMarkers(pmarkers);
  }, [mapPins]);

  const setShowPop = useCallback(
    (marker: MarkerType | null) => {
      if (marker) {
        dispatch(setSelectedMarker(marker));
        setLoading(true);
        dispatch(getPinDetails({ params: { id: marker.id } })).finally(() => {
          setLoading(false);
        });

        const map = mapRef.current;
        let anchor:
          | "top-left"
          | "top-right"
          | "bottom-left"
          | "center"
          | "bottom-right" = "bottom-left";
        let offset: [number, number] = [0, -20]; // Default upar shift

        if (map) {
          const container = map.getContainer().getBoundingClientRect();
          const projected = map.project([marker.longitude, marker.latitude]);

          const isTop = projected.y < container.height * 0.4;
          const isBottom = projected.y > container.height * 0.6;
          const isLeft = projected.x < container.width * 0.5;
          const isRight = projected.x > container.width * 0.5;
          const isMiddleY =
            projected.y >= container.height * 0.35 &&
            projected.y <= container.height * 0.7;

          // Middle case (center anchor)
          if (isMiddleY) {
            anchor = "center";
            offset = isLeft ? (offset = [160, 0]) : (offset = [-180, 0]);
          }
          // if card on top then check card is on left side or right side
          else if (isTop) {
            anchor = isLeft ? "top-left" : "top-right";
            offset = [0, 0]; // Neeche shift ho popup
          }
          // if card on bottom then check card is on left side or right side
          else if (isBottom) {
            anchor = isLeft ? "bottom-left" : "bottom-right";
            offset = [0, 0]; // Upar shift ho popup
          }
          // if card on left side then check card is on top or bottom
          else if (isLeft) {
            anchor = isTop ? "top-right" : "top-left";
            offset = [0, 0];
          } else if (isRight) {
            anchor = isLeft ? "bottom-left" : "bottom-right";
            offset = [0, 0];
          } else {
            anchor = "top-left";
            offset = [0, 0];
          }

          dispatch(
            setMapState({
              // latitude: marker.latitude,
              // longitude: marker.longitude,
              popupAnchor: anchor, //  Correct placement
              popupOffset: offset, //  Offset adjust ho gaya
            })
          );
        }
      } else {
        dispatch(setSelectedMarker(marker));
        dispatch(setPinDetails(marker));
      }
    },
    [dispatch]
  );

  console.log(pinDescription, "pin descrition in api response");
  const getMapStyle = (zoom: number) => {
    switch (mapStyle) {
      case "automatic": {
        return autoStyle(zoom);
      }
      case "satellite": {
        return satelliteView;
      }
      case "street": {
        return streetView;
      }
      case "outdoor": {
        return outdoorView;
      }
    }
  };

  function setViewState(state: ViewState, source: unknown) {
    console.log(state);
    if (source) {
      state.zoom = 10.6;
    }
    dispatch(setMapState(state));
  }

  const RenderStageMarker: FC<{
    projectType: string;
    starred?: boolean;
    pin_colour?: string;
  }> = ({ projectType, starred = false, pin_colour }) => {
    // Find the matching color based on projectClass
    // const pinData = pinDescription?.rows.find((p) => p.subTitle === projectClass);
    const pinColour = pin_colour; // ? pin_colour : "#FFFF00";
    // console.log(pin_colour,"line 221")

    return (
      <div className="flex flex-col items-center">
        <span
          className="text-[11px] font-sans py-1 px-4 rounded-full text-white flex items-center"
          style={{ backgroundColor: pinColour }}
        >
          {/* If starred is true, show the white star SVG */}
          {starred && (
            <svg
              width="14"
              height="14"
              viewBox="0 3 24 24"
              fill="white"
              xmlns="http://www.w3.org/2000/svg"
              className="mr-1"
            >
              <path d="M12 2l2.83 6.06L22 9.27l-5 4.87L18.66 22 12 18.69 5.34 22l1.66-7.86-5-4.87 7.17-1.21L12 2z" />
            </svg>
          )}
          {projectType}
        </span>
        <span
          className="w-0 h-0 border-l-[4px] border-r-[4px] border-t-[8px] border-transparent"
          style={{ borderTopColor: pinColour }} // Triangle pointer matches background
        ></span>
      </div>
    );
  };

  const RenderIconMarker: FC<{
    stageId: number;
    starred: boolean;
    pin_colour: string;
  }> = ({ stageId, starred, pin_colour }) => {
    console.log(stageId);

    // Find the matching color based on projectClass
    // const pinData = pinDescription?.rows.find((p) => p.subTitle === projectClass);
    const pinColor = pin_colour; //? pin_colour : "#FFFF00";

    return (
      <svg
        width="18"
        height="32"
        viewBox="0 0 25 32"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        {/* Background Pin Shape with dynamic color */}
        <path
          d="M12.5 0C5.588 0 0 5.588 0 12.549c0 1.814.49 3.725 1.03 5.049C4.803 25.686 12.205 31.96 12.5 31.96c.294 0 7.696-6.275 11.47-14.461.54-1.324 1.03-3.235 1.03-5.05C25 5.589 19.412 0 12.5 0z"
          fill={pinColor}
        />

        {/* If starred === true, show white star, else show white hut */}
        {starred ? (
          // Star icon in white
          <path
            d="M12.192 6.097a1.04 1.04 0 0 0-.96.612l-1.28 2.795-2.947.407c-.86.118-1.22 1.278-.59 1.9l2.154 2.13-.54 3.047c-.158.889.79 1.603 1.552 1.171l2.61-1.476 2.613 1.476c.763.431 1.711-.285 1.553-1.174l-.543-3.047 2.157-2.13c.63-.622.267-1.783-.593-1.9l-2.948-.405-1.28-2.795a1.041 1.041 0 0 0-.96-.612h.002z"
            fill="#FFFFFF"
          />
        ) : (
          // White Hut
          <path
            d="M6.513 10.63v7.251h4.402v-3.697h3.169v3.697h4.402V10.63l.639.328.482-.94L12.5 6.371l-7.108 3.644.483.941.638-.328z"
            fill="#FFFFFF"
          />
        )}
      </svg>
    );
  };

  function autoStyle(zoom: number) {
    if (zoom >= 16) {
      // Use satellite view for higher zoom levels
      // return "mapbox://styles/mapbox/satellite-v9";
      return "mapbox://styles/mapbox/streets-v11";
    } else {
      // Use streets view for lower zoom levels
      return "mapbox://styles/mapbox/streets-v11";
    }
  }
  console.log("new state shop pop", showPop);

  const markers = useMemo(
    () =>
      mapMarkers.map((marker) => {
        return marker.latitude && marker.longitude ? (
          <Marker
            className="!cursor-pointer"
            key={marker.id}
            longitude={marker.longitude}
            latitude={marker.latitude}
          >
            <div
              className="flex flex-col items-center !cursor-pointer"
              onClick={() => {
                setShowPop(marker);
              }}
            >
              {viewState.zoom >= 15 ? (
                <RenderStageMarker
                  // stageId={marker.project_status_id[0]}
                  projectType={marker.project_type_id[1]}
                  starred={marker?.starred ?? false}
                  pin_colour={marker?.pin_color}
                />
              ) : (
                <RenderIconMarker
                  stageId={marker.project_status_id[0]}
                  starred={marker?.starred ?? false}
                  pin_colour={marker?.pin_color as string}
                />
              )}
            </div>
          </Marker>
        ) : (
          ""
        );
      }),
    [mapMarkers, setShowPop, viewState.zoom]
  );

  const handleMoveEnd = () => {
    if (mapRef.current) {
      const newBounds = mapRef.current.getBounds();
      setCurrentBounds(newBounds);
    }
  };

  const filterBody = useSelector(
    (root: RootState) => root.rootReducer.mapViewReducer.filterBody
  );

  const searchString = useSelector(
    (root: RootState) => root.rootReducer.mapViewReducer.searchString
  );

  useEffect(() => {
    if (mapRef.current) {
      handleMoveEnd();
    }
  }, [open, viewState, filterBody, searchString]);

  useEffect(() => {
    console.log("current visible bounds", debouncedBounds);

    const body = {
      // ...filterBody,
      filters: {
        ...filterBody.filters,
        q: searchString ? [searchString] : undefined,
        coordinates: debouncedBounds ?? undefined,
      }, // Create a new filters object
    };

    // setLoading(true);
    // dispatch(setPage(1));
    dispatch(getMapPins({ params: {}, body }))
      .unwrap()
      .then((data) => {
        if (data?.pinDescription) {
          // console.log( data.pinDescription, "line 388");
          setPinDescription(data.pinDescription);
        } else {
          console.warn("pinDescription is missing in response.");
        }
      })
      .catch((error) => {
        console.error("Error fetching map pins:", error);
      });
  }, [debouncedBounds, dispatch, searchString]);

  useEffect(() => {
    const handleResize = () => {
      console.log("mapRef resize called");
      mapRef.current?.resize(); // Forces the map to adjust its size
    };

    const timeout = setTimeout(() => {
      handleResize();
    }, 300); // Match this with your sidebar animation duration

    return () => clearTimeout(timeout); // Cleanup the timeout on unmount or re-run
  }, [open, isCompanyDetailHidden]); // Only depend on 'open' state

  // useEffect(() => {
  //   if (!mapRef.current) return;

  //   // Initialize Mapbox map
  //   // const map = new mapboxgl.Map({
  //   //   container: mapContainerRef.current,
  //   //   style: "mapbox://styles/mapbox/streets-v11",
  //   //   center: [0, 0], // Default center
  //   //   zoom: 2,
  //   // });

  //   // Add Geolocate Control (Find My Location Button)
  //   const geolocateControl = new mapboxgl.GeolocateControl({
  //     positionOptions: { enableHighAccuracy: true },
  //     trackUserLocation: true, // Tracks user movement
  //     showUserLocation: true,
  //   });

  //   mapRef.current.addControl(geolocateControl);

  //   // Store map & geolocate control in refs
  //   // mapRef.current = map;
  //   geolocateRef.current = geolocateControl;
  //   // geolocateRef?.current?.trigger();
  // }, []);

  useEffect(() => {
    console.log("geolocateRef current", geolocateRef.current);
    if (geolocateRef.current)
      setTimeout(() => {
        geolocateRef?.current?.trigger();
      }, 1000);
  }, [geolocateRef.current]);

  useEffect(() => {
    const handleResize = () => {
      // mapRef.current?.resize(); // Forces the map to adjust its size
      // mapRef.current.relo
      console.log("handle resize for open");
      // const map = mapRef.current;
      // map?.resize();
      // map?.triggerRepaint();
      const map = mapRef.current?.getMap();
      if (map) {
        const currentStyle = map.getStyle()?.sprite;
        if (currentStyle) {
          map.setStyle(currentStyle);
          const bounds = map.getBounds();
          if (bounds) map.fitBounds(bounds, { padding: 20, animate: false });
          map.resize();
        }
      }
    };
    handleResize();
  }, [open]);

  return (
    <div
      ref={mapContainerRef}
      className="w-full h-full overflow-hidden relative z-1000"
    >
      <Map
        {...viewState}
        ref={mapRef}
        onMove={(evt) => {
          // @ts-expect-error - TODO: fix this
          setViewState(evt.viewState, evt.geolocateSource);
        }}
        onClick={() => setShowPop(null)}
        style={{ width: "100%", height: "100%" }}
        mapStyle={getMapStyle(viewState.zoom)}
        bearing={0}
        mapboxAccessToken={map_token}
        dragRotate={false}
      >
        <NavigationControl position="bottom-right" showCompass={false} />
        {/* <AttributionControl position="top-left" customAttribution="Map design by me"  /> */}
        <GeolocateControl ref={geolocateRef} position="bottom-right" />
        {markers}

        {showPop && showPop.length > 0 && (
          <Popup
            key={showPop[0].id}
            longitude={showPop[0].longitude}
            latitude={showPop[0].latitude}
            onClose={() => setShowPop(null)}
            closeOnClick={false}
            closeButton={false}
            focusAfterOpen={true}
            anchor={popupAnchor} // Redux se dynamic position
            maxWidth="350px"
            offset={popupOffset}
            className="z-20"
          >
            <CustomCloseButton onClose={() => setShowPop(null)} />
            <div className="h-fit relative ">
              <Carousel
                opts={{
                  align: "center",
                  loop: true,
                }}
                orientation="vertical"
                className="h-full"
              >
                <CarouselContent>
                  {showPop.map((pop, ind) => (
                    <CarouselItem className="relative" key={ind}>
                      <ProjectCard
                        className="w-[300px] max-h-[370px] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-100  "
                        lead={pop}
                      />

                      {showPop.length - (ind + 1) != 0 ? (
                        <div className="absolute left-[45%] bottom-2">
                          <Badge
                            variant="outline"
                            className="flex flex-col bg-white/80 hover:bg-white items-center"
                          >
                            {/* <span>{showPop.length - (ind + 1)} Leads</span> */}
                            <ChevronDown size={20} />
                          </Badge>
                        </div>
                      ) : showPop.length > 1 ? (
                        <div className="absolute top-2 left-[45%]">
                          <Badge
                            variant="outline"
                            className="flex flex-col bg-white/80 hover:bg-white items-center"
                          >
                            {/* <span>{showPop.length - (ind + 1)} Leads</span> */}
                            <ChevronUp size={20} />
                          </Badge>
                        </div>
                      ) : (
                        ""
                      )}
                    </CarouselItem>
                  ))}
                </CarouselContent>
                {/* <CarouselPrevious className="absolute left-2 bg-white/80 hover:bg-white" />
                  <CarouselNext className="absolute right-2 bg-white/80 hover:bg-white" /> */}
              </Carousel>
              {/* <ScrollBar /> */}

              {/* {showPop && showPop.length > 1 && (
                <div className="absolute left-[35%] bottom-2">
                  <Badge
                    variant="secondary"
                    className="flex flex-col w-[100px] py-1items-center"
                  >
                    <span>{showPop.length} Leads</span>
                    <ChevronDown size={20} />
                  </Badge>
                </div>
              )} */}
            </div>
          </Popup>
        )}
      </Map>

      {/* Dropdown for 'For Sale' */}
      <div className="absolute  bottom-7 right-12 bg-white rounded ">
        <Popover>
          <PopoverTrigger className="rounded" asChild>
            <Button variant="outline" className="p-2 rounded">
              Map{" "}
              <svg className="ml-2 h-4 w-4" viewBox="0 0 24 24" fill="none">
                <path
                  d="M5 9l7 7 7-7"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                ></path>
              </svg>
            </Button>
          </PopoverTrigger>
          <PopoverContent side="top" align="end" className="p-0">
            <MapOptions />
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
};

const CustomCloseButton = ({ onClose }: { onClose: () => void }) => {
  return (
    <button
      onClick={onClose}
      style={{
        position: "absolute",
        top: "0px",
        right: "0px",
        background: "transparent",
        border: "none",
        cursor: "pointer",
      }}
      className="z-30"
    >
      {/* <IoCloseCircleOutline size="20" /> */}
      <IoMdCloseCircle size="20" color="#eb5757" />
    </button>
  );
};

export default React.memo(MapView);
